import { h, k, s, u, radial, layout, Ancestry, Predicate } from '../common/Global_Imports';
import { G_Widget, G_Cluster, T_Kinship } from '../common/Global_Imports';
import type { Dictionary } from '../types/Types';
import { G_Paging } from './G_Paging';
import { get } from 'svelte/store';

export default class G_RadialGraph {
	g_parent_clusters: Dictionary<G_Cluster> = {};		// includes related
	g_child_clusters: Dictionary<G_Cluster> = {};

	destructor() {
		Object.values(this.g_parent_clusters).forEach(cluster => cluster.destructor());
		Object.values(this.g_child_clusters).forEach(cluster => cluster.destructor());
		this.g_parent_clusters = {};
		this.g_child_clusters = {};
	}

	static readonly _____LAYOUT: unique symbol;

	//////////////////////////////////////////////////
	// layout widgets, cluster lines, & paging arcs //
	//////////////////////////////////////////////////

	grand_layout_radial() {
		this.destructor();
		this.layout_forPoints_toChildren(true);
		this.layout_forPoints_toChildren(false);
		this.layout_focus();
		this.layout_forPaging();
	}

	layout_focus() {
		const ancestry = get(s.w_ancestry_focus);
		const g_focus = ancestry?.g_widget;
		if (!!g_focus && !!ancestry?.thing) {
			const width_ofTitle = ancestry.thing.width_ofTitle;
			const x = -7.5 - (width_ofTitle / 2);
			const y = -11;
			const origin_ofWidget = layout.center_ofGraphView.offsetByXY(x, y);
			g_focus.layout_widget();
			g_focus.width_ofWidget = width_ofTitle;
			g_focus.location_ofRadial = origin_ofWidget;
			g_focus.width_ofGraphDrawing = width_ofTitle + 30;
			g_focus.origin_ofRadial = origin_ofWidget.offsetByX(g_focus.widget_points_right ? 0 : -width_ofTitle);	// adjust for printing
		}
	}

	layout_forPoints_toChildren(points_toChildren: boolean) {
		const focus_ancestry = get(s.w_ancestry_focus);
		if (!!focus_ancestry) {
			if (points_toChildren) {
				let childAncestries = focus_ancestry.ancestries_createUnique_forKinship(T_Kinship.children);
				this.assignAncestries_toClusterFor(childAncestries, Predicate.contains, true);
			} else {
				for (const predicate of h?.predicates) {
					const ancestries = focus_ancestry.ancestries_createUnique_forKinship(predicate.kinship_forPoints_toChildren(false));
					this.assignAncestries_toClusterFor(ancestries, predicate, false);
				}
			}
		}	
	}

	static readonly _____CLUSTERS: unique symbol;

	get g_clusters(): Array<G_Cluster> {
		return u.concatenateArrays(Object.values(this.g_parent_clusters), Object.values(this.g_child_clusters));
	}

	get g_clusters_forPaging(): Array<G_Cluster> {
		return this.g_clusters.filter(cluster => cluster.ancestries.length > 0);
	}

	get g_cluster_atMouseLocation(): G_Cluster | null {
		for (const g_cluster of this.g_clusters) {
			if (g_cluster.isMouse_insideThumb) {
				return g_cluster;
			}
		}
		return null;
	}

	private g_cluster_forPredicate_toChild(predicate: Predicate, points_toChildren: boolean) : G_Cluster {
		const g_clusters = points_toChildren ? this.g_child_clusters : this.g_parent_clusters;;
		let g_cluster = g_clusters[predicate.kind];
		if (!g_cluster) {
			g_cluster = new G_Cluster(predicate, points_toChildren);
			g_clusters[predicate.kind] = g_cluster;
		}
		return g_cluster;
	}

	static readonly _____ANCESTRIES: unique symbol;

	get total_ancestries(): number {
		return this.g_clusters.reduce((sum, cluster) => sum + cluster.total_widgets, 0);
	}

	private assignAncestries_toClusterFor(ancestries: Array<Ancestry> | null, predicate: Predicate | null, points_toChildren: boolean) {
		if (!!predicate && !!ancestries) {
			const g_cluster = this.g_cluster_forPredicate_toChild(predicate, points_toChildren);
			g_cluster.setAncestries(ancestries);
			for (const ancestry of ancestries) {
				ancestry.g_widget.g_cluster = g_cluster;
			}
		}
	}

	static readonly _____NECKLACE: unique symbol;

	get visible_g_widgets(): G_Widget[] {
		let array: G_Widget[] = this.g_necklace_widgets;
		const g_focus = get(s.w_ancestry_focus)?.g_widget;
		if (!!g_focus) {
			array.push(g_focus);
		}
		return array
	}

	get g_necklace_widgets(): G_Widget[] {
		let g_widgets: G_Widget[] = [];
		for (const g_cluster of this.g_clusters) {
			if (!!g_cluster) {
				for (const g_widget of g_cluster.g_cluster_widgets) {
					g_widgets.push(g_widget);
				}
			}
		}
		return g_widgets;
	}

	static readonly _____PAGING: unique symbol;

	g_paging_forPredicate_toChildren(predicate: Predicate, points_toChildren: boolean): G_Paging | null {
		const g_thing_pages = radial.g_pages_forThingID(get(s.w_ancestry_focus)?.thing?.id);
		return g_thing_pages?.g_paging_forPredicate_toChildren(predicate, points_toChildren) ?? null;
	}

	layout_forPaging() {
		const radius = get(radial.w_resize_radius);
		const angle_per_widget = 40 / radius;			// Limit show so arc spread never exceeds 180°
		const maximum_portion = Math.floor(Math.PI / angle_per_widget);
		let remaining_toShow = Math.ceil(Math.pow(radius, 1.5) / k.height.row);
		if (this.total_ancestries > remaining_toShow || this.total_ancestries > maximum_portion) {
			let clusters = this.g_clusters_forPaging;
			clusters.sort((a, b) => a.total_widgets - b.total_widgets);		// sort clusters by widgets count, smallest first
			while (clusters.length > 0 && remaining_toShow > 0) {
				const portion = Math.min(Math.ceil(remaining_toShow / clusters.length), maximum_portion);
				const cluster = clusters[0];
				const show = Math.min(portion, cluster.total_widgets);
				remaining_toShow -= show;
				cluster.widgets_shown = show;
				clusters.shift();
			}
		}
		for (const g_cluster of this.g_clusters_forPaging) {
			g_cluster.layout_forPaging();
		}
	}

}

export const g_radial = new G_RadialGraph();
